import clsx from "clsx";
import { PageTopper } from "@/components/PageTopper";
import { ControlsBar } from "@/components/ControlsBar";
import { Menu } from "@/components/Menu";
import { MenuIcon } from "@/components/MenuIcon";
import { FooterCTA } from "@/components/FooterCTA";
import { AboutBlock } from "@/components/AboutBlock";
import { useUrls } from "@/hooks/useUrls";
import { getKeys } from "@/utils/keys";
import styles from "./styles.module.scss";

interface PageContainerProps {
  headline: string;
  dek?: string;
  footerCTASlug?: keyof ReturnType<typeof useUrls>;
  filters: React.ReactNode;
  activePageSlug: keyof ReturnType<typeof useUrls>;
  isLoading?: boolean;
}

export function PageContainer({
  headline,
  dek,
  footerCTASlug,
  filters,
  activePageSlug,
  isLoading,
  children,
}: React.PropsWithChildren<PageContainerProps>) {
  const urls = useUrls();

  return (
    <div>
      <div className={styles.page}>
        <div className={styles.sidebar}>
          <div className={styles["sidebar-top"]}>
            <header className={styles.header}>
              <h2 className={styles.title}>
                <a href={urls.home.url}>Global Trade Explorer</a>
              </h2>
              <p className={styles.tagline}>
                Visualize flows of trade around the&nbsp;world
              </p>
            </header>

            <nav className={styles.nav} aria-label="Global trade explorer">
              <ul className={styles["nav-list"]}>
                {getKeys(urls).map((key) => (
                  <li
                    key={key}
                    className={key === activePageSlug ? styles.active : ""}
                  >
                    <a
                      aria-current={key === activePageSlug ? "page" : undefined}
                      href={urls[key].url}
                      data-layer-event-prefix="Navigation Link"
                      data-layer-action="click"
                      data-layer-text={urls[key].title}
                      data-layer-region="side nav"
                    >
                      <span> {urls[key].title}</span>
                    </a>
                  </li>
                ))}
              </ul>
              <Menu
                className={styles["menu-btn"]}
                data-layer-region="side nav"
                data-layer-text="Browse visualizations"
              >
                <div className={styles["menu-btn-icon"]}>
                  <MenuIcon />
                </div>
                Browse&nbsp;<span>visualizations</span>
              </Menu>
            </nav>
          </div>

          <div className={styles["sidebar-bottom-desktop"]}>
            <AboutBlock />
          </div>
        </div>

        <div className={styles["main-wrap"]}>
          <main className={styles.main}>
            <div className={styles["main-inner"]}>
              <PageTopper headline={headline} dek={dek} />
              <ControlsBar isLoading={isLoading}>{filters}</ControlsBar>

              <div
                className={clsx(
                  styles["main-content"],
                  isLoading && styles["main-loading"]
                )}
              >
                {children}
              </div>
            </div>
          </main>
          {footerCTASlug && (
            <div className="main-footer-cta">
              <FooterCTA pageSlug={footerCTASlug} />
            </div>
          )}
          <div className={styles["main-bottom-mobile"]}>
            <AboutBlock />
          </div>
        </div>
      </div>
    </div>
  );
}
